import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, ToggleLeft, ToggleRight } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { z } from 'zod';

const cargoFormSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter no minimo 2 caracteres'),
});

type CargoFormData = z.infer<typeof cargoFormSchema>;

const allPermissoes = ['ADMIN', 'FINANCEIRO', 'CAIXA', 'COORDENADOR_CAMPANHA', 'COORDENADOR_EVENTO', 'VISUALIZADOR'] as const;

const perfilLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  FINANCEIRO: 'Financeiro',
  CAIXA: 'Caixa',
  COORDENADOR_CAMPANHA: 'Coord. Campanha',
  COORDENADOR_EVENTO: 'Coord. Evento',
  VISUALIZADOR: 'Visualizador',
};

const perfilColors: Record<string, string> = {
  ADMIN: 'bg-purple-100 text-purple-800',
  FINANCEIRO: 'bg-blue-100 text-blue-800',
  CAIXA: 'bg-green-100 text-green-800',
  COORDENADOR_CAMPANHA: 'bg-orange-100 text-orange-800',
  COORDENADOR_EVENTO: 'bg-teal-100 text-teal-800',
  VISUALIZADOR: 'bg-gray-100 text-gray-800',
};

export function CargosPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCargoId, setEditingCargoId] = useState<string | null>(null);
  const [selectedPermissoes, setSelectedPermissoes] = useState<string[]>([]);

  const utils = trpc.useUtils();

  const { data: cargosList, isLoading } = trpc.cargos.list.useQuery({});

  const createMutation = trpc.cargos.create.useMutation({
    onSuccess: () => {
      utils.cargos.list.invalidate();
      handleCloseModal();
    },
  });

  const updateMutation = trpc.cargos.update.useMutation({
    onSuccess: () => {
      utils.cargos.list.invalidate();
      handleCloseModal();
    },
  });

  const form = useForm<CargoFormData>({
    resolver: zodResolver(cargoFormSchema),
  });

  const isEditing = !!editingCargoId;

  const togglePermissao = (perm: string) => {
    setSelectedPermissoes((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const onSubmit = (data: CargoFormData) => {
    if (selectedPermissoes.length === 0) return;

    if (isEditing) {
      updateMutation.mutate({ id: editingCargoId!, nome: data.nome, permissoes: selectedPermissoes as any });
    } else {
      createMutation.mutate({ nome: data.nome, permissoes: selectedPermissoes as any });
    }
  };

  const handleEdit = (cargo: { id: string; nome: string; permissoes: string[]; ativo: boolean }) => {
    setEditingCargoId(cargo.id);
    setSelectedPermissoes(cargo.permissoes);
    form.reset({ nome: cargo.nome });
    setIsModalOpen(true);
  };

  const handleToggleStatus = (cargo: { id: string; ativo: boolean }) => {
    updateMutation.mutate({ id: cargo.id, ativo: !cargo.ativo });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCargoId(null);
    setSelectedPermissoes([]);
    form.reset();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Cargos</h1>
        <Button onClick={() => setIsModalOpen(true)} disabled={!canEdit}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Cargo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <p className="text-sm text-gray-500">
            Gerencie os cargos e suas permissoes de acesso
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Permissoes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cargosList?.map((cargo) => (
                  <TableRow key={cargo.id}>
                    <TableCell className="font-medium">{cargo.nome}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {cargo.permissoes.map((p) => (
                          <span key={p} className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${perfilColors[p]}`}>
                            {perfilLabels[p]}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                          cargo.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {cargo.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(cargo)} disabled={!canEdit} className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(cargo)}
                          disabled={!canEdit}
                          className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                          title={cargo.ativo ? 'Desativar' : 'Ativar'}
                        >
                          {cargo.ativo ? (
                            <ToggleRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-red-600" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={isEditing ? 'Editar Cargo' : 'Novo Cargo'}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Nome" {...form.register('nome')} error={form.formState.errors.nome?.message} />
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Permissoes</label>
            <div className="space-y-2 rounded-lg border border-gray-200 p-3">
              {allPermissoes.map((perm) => (
                <label key={perm} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPermissoes.includes(perm)}
                    onChange={() => togglePermissao(perm)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${perfilColors[perm]}`}>
                    {perfilLabels[perm]}
                  </span>
                </label>
              ))}
            </div>
            {selectedPermissoes.length === 0 && (
              <p className="mt-1 text-xs text-red-500">Selecione ao menos uma permissao</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={isPending} disabled={selectedPermissoes.length === 0}>
              {isEditing ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CargosPage;
